import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

import { cn } from "../../utils/cn";

/**
 * Typography variants. Hero / h1 stay on the Rhymes Display editorial
 * ladder; h2 / h3 step down into ABC Social for dense-UI hierarchy:
 *
 *   Display-editorial (Rhymes Display, extralight 200, responsive):
 *     hero · h1   — marketing/landing, editorial headers, roomy main-app
 *                   hero titles.
 *
 *   Sans-dense (ABC Social, non-responsive):
 *     h2          — dashboard / console section titles. Bold (700), 20px.
 *     h3          — list-row / sub-section titles. Normal (400), 18px.
 *                   Rhymes Display only ships at extralight; the sans
 *                   ladder gives h2 / h3 legible weights in compact UIs.
 *
 *   Prose / meta: body · small · fine-print · label
 *   Numeric:      number (inline) · number-lg (page-dominant) ·
 *                 number-hero (hero-scale financial figure)
 */
const textVariants = cva("", {
    variants: {
        variant: {
            // Display ladder — editorial, extralight, responsive
            hero: "font-display font-extralight text-3xl leading-none tracking-tight-3xl md:text-7xl md:tracking-tight-7xl",
            h1: "font-display font-extralight text-2xl leading-8 tracking-tight-2xl md:text-4xl md:leading-10 md:tracking-tight-4xl",
            // h2 is the dense-section header — sans-bold, responsive (xl mobile → 2xl desktop).
            h2: "font-sans font-bold text-xl leading-7 md:text-2xl md:leading-8 tracking-[-0.5px]",
            // h3 steps down into ABC Social normal — sans-normal, responsive (lg mobile → xl desktop).
            h3: "font-sans font-normal text-lg leading-7 tracking-[-0.45px] md:text-xl md:tracking-[-0.5px]",

            // Prose
            body: "font-sans font-normal text-base leading-6 md:text-lg md:leading-7",
            small: "font-sans font-normal text-sm leading-5 md:text-base md:leading-6",
            "fine-print":
                "font-sans font-normal text-xs leading-4 md:text-sm md:leading-5",
            label: "font-sans font-normal text-xs leading-4",

            // Numeric — `font-numeric` is APK Narrative (proportional)
            // so punctuation (`,` `.`) reads narrow. Live count-ups
            // need to override to `font-numeric-mono` (APK Narrative
            // Mono) at the consumer level — see
            // `RetailPendingEarningsTicker`. The Figma typography
            // spec for these variants assumed APK Narrative Mono +
            // a tighter -0.1em tracking ladder; with proportional
            // APK Narrative we keep Tailwind's defaults instead, so
            // the static rendering looks clean without the heavy
            // negative tracking that mono needed.
            //
            // number: inline values inside tables / cells.
            number: "font-numeric font-normal text-sm leading-5 md:text-base md:leading-6",
            // number-lg: page-dominant financial figure — the hero number on a
            // dashboard (e.g. total earnings). Max ~2 per page.
            //
            // Synced with Figma nodes 692:989 (mobile) + 685:991 (desktop):
            //   mobile  → 30px / 36 · tracking -0.75
            //   desktop → 36px / 40 · tracking -0.9
            // Figma's desktop spec specifies APK Narrative Mono, but
            // the variant stays on proportional `font-numeric` per the
            // ladder-wide convention noted above — consumers opt into
            // `font-numeric-mono` at the call site only for special
            // cases (live count-up animations where stable digit
            // widths matter, e.g. `RetailPendingEarningsTicker`).
            "number-lg":
                "font-numeric font-normal text-3xl leading-9 tracking-tight-3xl md:text-4xl md:leading-10 md:tracking-tight-4xl",
            // number-hero: same responsive sizing as `hero`, but APK Narrative
            // instead of Rhymes Display. Used for the live count-up dollar
            // figure that needs `hero`'s scale but stable digit widths
            // (Rhymes Display's variable letter widths jitter visibly during
            // a count-up animation). `tabular-nums` keeps the digit columns
            // aligned during ticking. APK Narrative ships at weight 400 only,
            // so `font-normal` replaces hero's `font-extralight`.
            "number-hero":
                "font-numeric font-normal text-4xl leading-10 tracking-tight-4xl md:text-7xl md:leading-none md:tracking-tight-7xl tabular-nums"
        }
    },
    defaultVariants: {
        variant: "body"
    }
});

type TextVariant = NonNullable<VariantProps<typeof textVariants>["variant"]>;

// Default semantic element per variant. `hero` is the page-level headline (→ <h1>);
// other heading variants use their matching heading tag; body/small/fine-print are
// block prose (→ <p>); label/number are inline (→ <span>). Override via `render`
// when the caller needs a different element (e.g. h1 inside an h2 slot, or body
// copy rendered as a <div> when a <p> would nest illegally).
const DEFAULT_TAG: Record<TextVariant, "h1" | "h2" | "h3" | "p" | "span"> = {
    hero: "h1",
    h1: "h1",
    h2: "h2",
    h3: "h3",
    body: "p",
    small: "p",
    "fine-print": "p",
    label: "span",
    number: "span",
    "number-lg": "span",
    "number-hero": "span"
};

function Text({
    className,
    variant = "body",
    render,
    ...props
}: useRender.ComponentProps<"span"> &
    VariantProps<typeof textVariants>): React.ReactElement {
    return useRender({
        defaultTagName: DEFAULT_TAG[variant ?? "body"],
        props: mergeProps<"span">(
            {
                className: cn(textVariants({ variant }), className)
            },
            props
        ),
        render,
        state: {
            slot: "text",
            variant
        }
    }) as React.ReactElement;
}

export { Text, textVariants };
